import React, { useState, useMemo, useEffect } from 'react';
import _get from 'lodash/get';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Box } from '@mui/material';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlDropdownMenu, SlDropdownMenuProps } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { apiGetProjects } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import { ProjectRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { useAppSelector } from 'src/utils/Hooks';
import colors from 'src/utils/colors';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_TABS, BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useProjectsSubscription } from './hooks';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import { StacklineDefaultProfileImage, RightArrow } from 'src/components/SvgIcons/SvgIcons';
import { useProjectFlowStore } from '../stores/projectFlowStore';
import { NoDataPlaceHolderTableRow } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { useContentGenerationStore } from '../Generate/components/ProductTable/store';
import { useProjects } from './hooks/useProjects';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';

const DropdownMenu = (props: SlDropdownMenuProps) => {
  return (
    <SlDropdownMenu
      menuItemTextStyles={{ fontWeight: 500 }}
      iconStyle={{ marginLeft: '10px' }}
      menuStyles={{
        maxHeight: '288px',
        '& .MuiMenuItem-root': {
          paddingTop: '8px',
          paddingBottom: '8px'
        }
      }}
      {...props}
    />
  );
};

const ProjectEmptyState: React.FC = () => {
  const { beaconNavigator } = useBeaconNavigator();
  const { clearSelection } = useContentGenerationStore();
  const { clearProjectFlow } = useProjects();

  const handleAddProject = () => {
    clearSelection();
    clearProjectFlow();
    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROJECTS,
      subtab: BEACON_SUBTABS.PROJECTS_SETTINGS
    });
    beaconNavigator.go();
  };

  return (
    <Flex
      justifyContent="center"
      marginTop="48px"
      sx={{
        '& .no-data-placeholder-table-row': {
          padding: '0 !important'
        },
        '& .no-data-placeholder-table-row-title': {
          marginTop: '20px !important',
          '& .MuiTypography-root': {
            fontSize: '20px !important'
          }
        },
        '& .no-data-placeholder-table-row-description': {
          fontSize: '16px',
          fontWeight: 400,
          marginTop: '8px'
        }
      }}
    >
      <div style={{ width: '264px' }}>
        <NoDataPlaceHolderTableRow
          title="Add Project"
          description="There are currently no projects to display. All of your projects will be available on this page."
          link={
            <Text
              variant="body2"
              link
              onClick={handleAddProject}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 500,
                marginTop: '4px',
                fontSize: '16px'
              }}
            >
              Create a project
              <RightArrow style={{ height: '12px', width: '12px' }} />
            </Text>
          }
        />
      </div>
    </Flex>
  );
};

interface UserAvatarProps {
  name: string;
  displayName: string;
  updatedAt?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ name, displayName, updatedAt }) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `Updated on ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })}`;
  };

  const initials = useMemo(() => {
    const nameParts = displayName.split(' ');
    return name === 'content-generation-workflow' ? 'SL' : `${_get(nameParts[0], 0, '')}${_get(nameParts[1], 0, '')}`;
  }, [displayName, name]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '1px' }}>
      <AppProfileIcon
        size={30}
        userId={name === 'content-generation-workflow' ? undefined : name}
        customIcon={name === 'content-generation-workflow' ? <StacklineDefaultProfileImage /> : undefined}
        initials={initials}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: colors.darkBlue,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {displayName}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '20px',
            color: colors.grey,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {formatDate(updatedAt)}
        </Typography>
      </Box>
    </Box>
  );
};

interface ProjectRowProps {
  project: ProjectRecord;
  isFirst: boolean;
  isLast: boolean;
  getUserDisplayName: (userId: string) => string;
  onProjectClick: (projectId: string) => void;
}

const ProjectRow: React.FC<ProjectRowProps> = ({ project, isFirst, isLast, getUserDisplayName, onProjectClick }) => {
  const handleClick = () => {
    onProjectClick(project.projectId);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        gap: '104px',
        alignItems: 'center',
        height: '60px',
        padding: '0',
        paddingLeft: '16px',
        borderBottom: isLast ? 'none' : `1px solid ${colors.graysSecondary}`,
        cursor: 'pointer',
        '&:hover .project-title': {
          textDecoration: 'underline'
        }
      }}
    >
      {/* Owned By Column */}
      <Box sx={{ width: '179px' }}>
        <UserAvatar
          name={project.createdBy}
          displayName={getUserDisplayName(project.createdBy)}
          updatedAt={project.updatedAt || project.createdAt}
        />
      </Box>

      {/* Project Title Column */}
      <Box sx={{ width: '241px' }}>
        <Typography
          className="project-title"
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: colors.darkBlue,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {project.name}
        </Typography>
      </Box>

      {/* Profile Column */}
      <Box>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: colors.darkBlue,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {project.profileName || 'No Profile Assigned'}
        </Typography>
      </Box>
    </Box>
  );
};

const ProjectTable: React.FC = () => {
  useProjectsSubscription();
  const [filterMode, setFilterMode] = useState('ALL');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const userId = useAppSelector((state) => state.user.session?.userId);
  const { beaconNavigator } = useBeaconNavigator();
  const { setProjectDetails, setSelectedRetailerSkus } = useProjectFlowStore();
  const { clearSelection } = useContentGenerationStore();

  useEffect(() => {
    clearSelection();
    setSelectedRetailerSkus([]);
  }, [clearSelection, setSelectedRetailerSkus]);

  // Fetch projects data with infinite query and load pages as needed.
  const {
    data: projectsResponse,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['projects', filterMode, userId, pageSize],
    queryFn: ({ pageParam = 1 }) =>
      apiGetProjects({
        createdBy: filterMode === 'OWNED' ? userId : undefined,
        pageNumber: pageParam,
        pageSize
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((count, currentPage) => count + currentPage.data.records.length, 0);
      return loadedCount < lastPage.data.totalCount ? allPages.length + 1 : undefined;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!userId || filterMode === 'ALL'
  });

  const projects = useMemo(
    () => projectsResponse?.pages.flatMap((currentPage) => currentPage.data.records) || [],
    [projectsResponse?.pages]
  );
  const totalCount = projectsResponse?.pages[0]?.data?.totalCount || 0;

  useEffect(() => {
    const requiredRecords = page * pageSize;
    const hasEnoughRecordsForCurrentPage = projects.length >= requiredRecords || projects.length >= totalCount;

    if (!hasEnoughRecordsForCurrentPage && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [page, pageSize, projects.length, totalCount, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Extract unique user IDs from projects for user metadata query
  const userIds = useMemo(() => {
    if (!projects.length) return [];
    const allUserIds = new Set<string>();
    projects.forEach((project) => {
      if (project.createdBy && project.createdBy !== 'content-generation-workflow') {
        allUserIds.add(project.createdBy);
      }
      if (project.updatedBy && project.updatedBy !== 'content-generation-workflow') {
        allUserIds.add(project.updatedBy);
      }
    });
    return Array.from(allUserIds);
  }, [projects]);

  // Fetch user metadata to get display names
  const { data: userMetadata, isLoading: isLoadingUserMetadata } = useQuery({
    queryKey: ['userMetadata', userIds],
    queryFn: async () => {
      if (userIds.length === 0) return {};
      const response = await axios.post('/api/user/GetUserProfilesFromUserIds', userIds);
      return response.data as Record<string, { firstName: string; lastName: string; email: string }>;
    },
    enabled: userIds.length > 0,
    staleTime: 5 * 60 * 1000
  });

  // Helper function to get user display name
  const getUserDisplayName = (userId: string) => {
    if (userId === 'content-generation-workflow') {
      return 'Stackline';
    }
    const userData = userMetadata?.[userId];
    if (userData?.firstName && userData?.lastName) {
      return `${userData.firstName} ${userData.lastName}`;
    }
    return userId;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const paginatedProjects = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return projects.slice(startIndex, endIndex);
  }, [projects, page, pageSize]);

  const isLoadingCurrentPageData = projects.length < page * pageSize && projects.length < totalCount;
  const isProjectsLoading = isLoading || isFetchingNextPage || isLoadingCurrentPageData;

  const handleFilterChange = (option: { id: string; label: string }) => {
    setFilterMode(option.id);
    setPage(1); // Reset to first page when filtering
  };

  const handleClickNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleClickPrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleProjectClick = (projectId: string) => {
    const project = projects.find((p) => p.projectId === projectId);

    if (project) {
      setProjectDetails({
        profileId: project.profileId,
        profileName: project.profileName,
        profileVersion: project.profileVersion
      });
    }

    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROJECTS,
      subtab: BEACON_SUBTABS.EXISTING_REVIEW,
      projectId,
      profileId: project.profileId,
      profileVersion: String(project.profileVersion)
    });
    beaconNavigator.go();
  };

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={32}>
        {/* Header */}
        <Flex flexDirection="column" gap={16}>
          <Flex flexDirection="column" gap={8}>
            <Text
              variant="h4"
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                lineHeight: '28px',
                color: colors.darkBlue
              }}
            >
              Content Projects
            </Text>
            <Text
              variant="body1"
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '24px',
                color: colors.darkBlue
              }}
            >
              Projects are content generation campaigns that apply the same profile to a group of products. Manage
              projects by editing an existing or creating a new one.
            </Text>
          </Flex>
        </Flex>

        {/* Filter Controls */}
        <Flex justifyContent="flex-end" alignItems="center">
          <Flex gap={16} alignItems="center">
            <DropdownMenu
              selectedId={filterMode}
              options={[
                { id: 'ALL', label: 'View All' },
                { id: 'OWNED', label: 'View Owned' }
              ]}
              onChange={handleFilterChange}
            />
          </Flex>
        </Flex>

        {/* Table or Empty State */}
        {!isProjectsLoading && !error && totalCount === 0 ? (
          // Empty state - replaces entire table when no projects exist
          <ProjectEmptyState />
        ) : (
          <Flex flexDirection="column" gap={24} alignItems="flex-end">
            <TableContainer
              sx={{
                backgroundColor: 'transparent',
                overflow: 'visible'
              }}
            >
              <Table
                sx={{
                  minWidth: 650,
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  backgroundColor: 'transparent',
                  '& thead .MuiTableCell-root': {
                    padding: '0px 0px 16px 16px',
                    border: 'none',
                    backgroundColor: 'transparent'
                  },
                  '& tbody .MuiTableCell-root': {
                    padding: '0px',
                    height: '60px',
                    border: 'none',
                    borderBottom: 'none'
                  },
                  '& tbody tr': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '280px' }}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: '20px',
                          letterSpacing: '1.2px',
                          color: colors.darkBlue,
                          textTransform: 'uppercase'
                        }}
                      >
                        OWNED BY
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '345px' }}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: '20px',
                          letterSpacing: '1.2px',
                          color: colors.darkBlue,
                          textTransform: 'uppercase'
                        }}
                      >
                        PROJECT TITLE
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: '20px',
                          letterSpacing: '1.2px',
                          color: colors.darkBlue,
                          textTransform: 'uppercase'
                        }}
                      >
                        PROFILE
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ padding: 0, border: 'none' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          border: `1px solid ${colors.graysSecondary}`,
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                      >
                        {error ? (
                          // Error state
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                              padding: 4,
                              backgroundColor: 'white'
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                color: colors.errorRed
                              }}
                            >
                              Failed to load projects. Please try again.
                            </Typography>
                            <Box
                              component="button"
                              onClick={() => refetch()}
                              sx={{
                                padding: '8px 16px',
                                backgroundColor: colors.darkBlue,
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                '&:hover': {
                                  backgroundColor: colors.darkBlue
                                }
                              }}
                            >
                              Retry
                            </Box>
                          </Box>
                        ) : isProjectsLoading || (paginatedProjects.length > 0 && isLoadingUserMetadata) ? (
                          // Loading rows
                          Array.from({ length: 10 }).map((_, index) => {
                            const isLast = index === 9;

                            return (
                              <Box
                                key={`loading-${index}`}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '60px',
                                  padding: '0',
                                  paddingLeft: '16px',
                                  borderBottom: isLast ? 'none' : `1px solid ${colors.graysSecondary}`,
                                  backgroundColor: 'transparent'
                                }}
                              >
                                {/* Loading skeleton content */}
                                <Box sx={{ width: '280px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: '50%',
                                        backgroundColor: colors.skeletonGrey
                                      }}
                                    />
                                    <Box>
                                      <Box
                                        sx={{
                                          width: 120,
                                          height: 14,
                                          backgroundColor: colors.skeletonGrey,
                                          mb: 0.5,
                                          borderRadius: '4px'
                                        }}
                                      />
                                      <Box
                                        sx={{
                                          width: 100,
                                          height: 12,
                                          backgroundColor: colors.skeletonGrey,
                                          borderRadius: '4px'
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                                <Box sx={{ width: '380px' }}>
                                  <Box
                                    sx={{
                                      width: 200,
                                      height: 14,
                                      backgroundColor: colors.skeletonGrey,
                                      borderRadius: '4px'
                                    }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Box
                                    sx={{
                                      width: 250,
                                      height: 14,
                                      backgroundColor: colors.skeletonGrey,
                                      borderRadius: '4px'
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })
                        ) : paginatedProjects.length > 0 ? (
                          paginatedProjects.map((project: ProjectRecord, index) => (
                            <ProjectRow
                              key={project.projectId}
                              project={project}
                              isFirst={index === 0}
                              isLast={index === paginatedProjects.length - 1}
                              getUserDisplayName={getUserDisplayName}
                              onProjectClick={handleProjectClick}
                            />
                          ))
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '120px',
                              backgroundColor: 'white'
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                color: colors.grey
                              }}
                            >
                              No projects found.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Flex justifyContent="flex-end" sx={{ padding: '16px 0' }}>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onClickNext={handleClickNext}
                  onClickPrevious={handleClickPrevious}
                  disableNextButton={page >= totalPages || isFetchingNextPage}
                />
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </BasePageContainer>
  );
};

export default ProjectTable;
